import { PlatformQuirks } from './platformQuirks';
import { DeviceLite, DeviceSnapshot, EXPLICIT_SWITCH_COOLDOWN_MS, UNPAIRED_INPUT_TTL_MS, isHandsFreeLabel, isHeadsetLabel, isRealDeviceId, normalizeLabel, stripDefaultPrefix } from './shared';

// Pure device decisions: which output belongs to a microphone, and what a device change means.

export interface SnapshotDiff {
    inputsAdded: DeviceLite[];      // real hardware ids only
    inputsRemoved: DeviceLite[];
    outputsAdded: DeviceLite[];
    outputsRemoved: DeviceLite[];
    defaultInputChanged: { fromLabel: string; toLabel: string } | null;
    defaultOutputChanged: { fromLabel: string; toLabel: string } | null;
    currentMicLost: boolean;
}

// Reason strings consumed by BaseAgentPage.OnAudioDeviceChangedAsync (via VoiceCallService.OnDeviceChangedAsync):
//   "mic-disconnected"  → C# switches to fallbackDeviceId (or the first available mic)
//   "new-paired-device" → C# auto-switches to fallbackDeviceId unless it is an earpiece
//   "device-change"     → C# refreshes the device list only, no switch
export type NotifyReason = 'mic-disconnected' | 'new-paired-device' | 'device-change';

// 'repair-output': re-run mic→speaker pairing (mobile default-route churn or output loss).
export type InternalAction = 'repair-output';

export interface ChangeDecision {
    reason: NotifyReason;
    // Must be a real hardware id (never "default"/"communications"): the C# picker strips
    // virtual aliases, so an alias id would never match an entry in AvailableMicrophones.
    fallbackDeviceId: string | null;
    internalActions: InternalAction[];
    rule: string;
    logProps?: Record<string, any>;
    // Input id that was auto-switched to (or suppressed by cooldown) — excluded from
    // late-pairing tracking.
    handledInputId: string | null;
}

//#region Mic ↔ speaker pairing (pure)
// True when the input has a real output counterpart by hardware group or normalized label.
// Used to decide whether a newly-arrived input is a headset (mic+speaker) rather than a
// webcam-style mic-only device that should not trigger an auto-switch on desktop.
export function hasStrictOutputMatch(input: DeviceLite, snapshot: DeviceSnapshot): boolean {
    const realOutputs = snapshot.outputs.filter(o => isRealDeviceId(o.deviceId));
    // USB / 3.5mm headsets expose the same hardware group for mic and speaker.
    if (input.groupId && realOutputs.some(o => o.groupId === input.groupId)) return true;
    // Bluetooth headsets carry different groupIds for HFP mic and A2DP speaker but share
    // the same normalized device name.
    const inputKey = normalizeLabel(input.label);
    return !!inputKey && realOutputs.some(o => normalizeLabel(o.label) === inputKey);
}

export interface PairingResult {
    mic: DeviceLite | null;
    output: DeviceLite | null;
}

// Resolves the output device that belongs to a microphone, so selecting a mic always
// brings its own speaker along. Priority: OS-default headset → hardware group →
// normalized label → same headset status → communications/default/first.
// When `explicit` is set (user picked the mic themselves), the OS-default-headset shortcut
// is skipped: the user's chosen mic dictates the output kind. Otherwise picking the
// built-in mic would still route audio to e.g. a lingering "Headphones (Realtek)" jack
// endpoint that Windows reports as default even with nothing plugged in.
export function pairOutputForInput(
    micId: string | null,
    snapshot: DeviceSnapshot,
    opts?: { explicit?: boolean }): PairingResult {
    const { inputs, outputs } = snapshot;
    if (outputs.length === 0) return { mic: null, output: null };

    const mic =
        (micId ? inputs.find(d => d.deviceId === micId) : undefined) ||
        inputs.find(d => d.deviceId === 'default') ||
        inputs[0] ||
        null;
    if (!mic) return { mic: null, output: null };

    const micIsHeadset = isHeadsetLabel(mic.label);
    const micKey = normalizeLabel(mic.label);
    let target: DeviceLite | undefined;

    // The OS default output's real hardware entry — the device the user actually hears.
    const defaultOutput = outputs.find(o => o.deviceId === 'default');
    const defaultReal = defaultOutput
        ? outputs.find(o => isRealDeviceId(o.deviceId) && o.label.trim() === stripDefaultPrefix(defaultOutput.label))
        : undefined;

    // 0) If the OS default output is a headset, always prefer its real hardware entry —
    //    but never against an explicit user choice (group/label rules below decide then).
    if (!opts?.explicit && defaultReal && isHeadsetLabel(defaultReal.label)) {
        target = defaultReal;
    }

    // Candidate pools (real hardware ids only).
    const realOutputs = outputs.filter(o => isRealDeviceId(o.deviceId));
    const sameGroup = mic.groupId ? realOutputs.filter(o => o.groupId && o.groupId === mic.groupId) : [];
    const sameLabel = micKey ? realOutputs.filter(o => normalizeLabel(o.label) === micKey) : [];

    // 1) Correlated outputs — same connector (group) or same controller (label) as the mic.
    //    Bluetooth hands-free mics come first: HFP capture only works while the hands-free
    //    profile owns the link, so playback must go to the matching "Headset (...)" output —
    //    routing to the A2DP "Headphones (...)" sibling leaves the mic silent.
    //    Otherwise the OS default wins INSIDE this pool: combo-jack headset mics carry no
    //    headset hint in their label ("Microphone (Realtek)"), but Windows flips the default
    //    output to the jack's "Headphones (Realtek)" the moment something is plugged in —
    //    and back to "Speakers" when the jack is empty — so the default disambiguates a
    //    plugged headset from a phantom jack endpoint where labels alone cannot.
    if (!target) {
        const correlated = [...sameGroup, ...sameLabel.filter(o => !sameGroup.includes(o))];
        if (correlated.length > 0) {
            target =
                (isHandsFreeLabel(mic.label) ? correlated.find(o => isHandsFreeLabel(o.label)) : undefined) ||
                (defaultReal && correlated.some(o => o.deviceId === defaultReal.deviceId) ? defaultReal : undefined) ||
                correlated.find(o => isHeadsetLabel(o.label) === micIsHeadset) ||
                correlated[0];
        }
    }

    // 2) No correlated output — status-matched fallback: OS default first (enumeration
    //    order is not stable, so an "anywhere" scan alone picks silent endpoints like
    //    monitor HDMI audio intermittently), then anywhere.
    if (!target) {
        target =
            (defaultReal && isHeadsetLabel(defaultReal.label) === micIsHeadset ? defaultReal : undefined) ||
            realOutputs.find(o => isHeadsetLabel(o.label) === micIsHeadset);
    }

    // 3) Absolute fallback: communications → default → first output
    if (!target) {
        target =
            outputs.find(o => o.deviceId === 'communications') ||
            outputs.find(o => o.deviceId === 'default') ||
            outputs[0];
    }

    return { mic, output: target || null };
}
//#endregion

//#region Device-change detection (pure)
export function diffSnapshots(
    prev: DeviceSnapshot,
    next: DeviceSnapshot,
    currentMicId: string | null,
    micActive: boolean): SnapshotDiff {

    const prevInputIds = new Set(prev.inputs.filter(d => isRealDeviceId(d.deviceId)).map(d => d.deviceId));
    const nextInputIds = new Set(next.inputs.filter(d => isRealDeviceId(d.deviceId)).map(d => d.deviceId));
    const prevOutputIds = new Set(prev.outputs.filter(d => isRealDeviceId(d.deviceId)).map(d => d.deviceId));
    const nextOutputIds = new Set(next.outputs.filter(d => isRealDeviceId(d.deviceId)).map(d => d.deviceId));

    return {
        inputsAdded: next.inputs.filter(d => isRealDeviceId(d.deviceId) && !prevInputIds.has(d.deviceId)),
        inputsRemoved: prev.inputs.filter(d => isRealDeviceId(d.deviceId) && !nextInputIds.has(d.deviceId)),
        outputsAdded: next.outputs.filter(d => isRealDeviceId(d.deviceId) && !prevOutputIds.has(d.deviceId)),
        outputsRemoved: prev.outputs.filter(d => isRealDeviceId(d.deviceId) && !nextOutputIds.has(d.deviceId)),
        defaultInputChanged: next.defaultInputLabel && next.defaultInputLabel !== prev.defaultInputLabel
            ? { fromLabel: prev.defaultInputLabel, toLabel: next.defaultInputLabel }
            : null,
        defaultOutputChanged: next.defaultOutputLabel && next.defaultOutputLabel !== prev.defaultOutputLabel
            ? { fromLabel: prev.defaultOutputLabel, toLabel: next.defaultOutputLabel }
            : null,
        currentMicLost: micActive && !!currentMicId && !next.inputs.some(d => d.deviceId === currentMicId)
    };
}

export interface PolicyContext {
    snapshot: DeviceSnapshot;
    currentMicId: string | null;
    currentSinkId: string | null;
    micActive: boolean;
    isMobile: boolean;
    cooldownActive: boolean;
    msSinceExplicitSwitch: number;
    recentUnpairedInputs: ReadonlyMap<string, number>;
    now: number;
    quirks: PlatformQuirks;
}

// Maps a snapshot diff to exactly one decision. A notification is ALWAYS produced per
// debounced burst — the explicit-switch cooldown only downgrades switch-triggering reasons
// to a plain 'device-change' list refresh, so the UI modal never goes stale.
export function decideChange(diff: SnapshotDiff, ctx: PolicyContext): ChangeDecision {
    const decision = decideNotification(diff, ctx);
    // Independent of the notification: if the output we are currently routed to vanished,
    // re-run output pairing right away so the browser gets a concrete replacement sink
    // instead of lingering on a dead AudioContext route.
    if (!ctx.isMobile && ctx.currentSinkId &&
        diff.outputsRemoved.some(o => o.deviceId === ctx.currentSinkId)) {
        decision.internalActions.unshift('repair-output');
    }
    return decision;
}

export function decideNotification(diff: SnapshotDiff, ctx: PolicyContext): ChangeDecision {
    const noSwitch = (rule: string, logProps?: Record<string, any>): ChangeDecision => ({
        reason: 'device-change',
        fallbackDeviceId: null,
        internalActions: [],
        rule,
        logProps,
        handledInputId: null
    });

    // 1) The active mic disappeared → fall back to the first real input.
    if (diff.currentMicLost) {
        if (ctx.cooldownActive && ctx.quirks.suppressAllDuringCooldown) {
            return noSwitch('mic-lost-suppressed-during-cooldown', {
                lostMicId: ctx.currentMicId,
                msSinceExplicit: ctx.msSinceExplicitSwitch
            });
        }
        const fallback = ctx.snapshot.inputs.find(d => isRealDeviceId(d.deviceId)) ?? null;
        return {
            reason: 'mic-disconnected',
            fallbackDeviceId: fallback?.deviceId ?? null,
            internalActions: [],
            rule: 'current-mic-lost',
            logProps: { lostMicId: ctx.currentMicId },
            handledInputId: null
        };
    }

    // 2) A new input arrived that qualifies as a headset. On mobile the OS owns output
    //    routing so any new non-excluded input qualifies; on desktop it must have a
    //    paired output so webcam-style mic-only devices are never auto-selected.
    const eligibleNew = diff.inputsAdded.find(d =>
        !ctx.quirks.excludeAsAutoSwitchTarget(d.label) &&
        (ctx.isMobile || hasStrictOutputMatch(d, ctx.snapshot)));
    if (eligibleNew) {
        if (ctx.cooldownActive) {
            const decision = noSwitch('new-paired-suppressed-during-cooldown', {
                suppressedDeviceId: eligibleNew.deviceId,
                suppressedLabel: eligibleNew.label,
                msSinceExplicit: ctx.msSinceExplicitSwitch,
                cooldownMs: EXPLICIT_SWITCH_COOLDOWN_MS
            });
            decision.handledInputId = eligibleNew.deviceId;
            return decision;
        }
        return {
            reason: 'new-paired-device',
            fallbackDeviceId: eligibleNew.deviceId,
            internalActions: [],
            rule: 'input-added',
            logProps: { label: eligibleNew.label },
            handledInputId: eligibleNew.deviceId
        };
    }

    // 3) An output arrived that completes the pairing for a recently-seen unpaired input
    //    (Bluetooth HFP mic enumerates before the A2DP output).
    if (!ctx.isMobile && diff.outputsAdded.length > 0) {
        for (const [id, firstSeen] of ctx.recentUnpairedInputs) {
            if (ctx.now - firstSeen > UNPAIRED_INPUT_TTL_MS) continue;
            const input = ctx.snapshot.inputs.find(d => d.deviceId === id);
            if (!input || ctx.quirks.excludeAsAutoSwitchTarget(input.label)) continue;
            if (!hasStrictOutputMatch(input, ctx.snapshot)) continue;
            if (ctx.cooldownActive) {
                const decision = noSwitch('late-pair-suppressed-during-cooldown', {
                    suppressedDeviceId: input.deviceId,
                    suppressedLabel: input.label,
                    msSinceExplicit: ctx.msSinceExplicitSwitch
                });
                decision.handledInputId = input.deviceId;
                return decision;
            }
            return {
                reason: 'new-paired-device',
                fallbackDeviceId: input.deviceId,
                internalActions: [],
                rule: 'output-completed-pairing',
                logProps: { label: input.label },
                handledInputId: input.deviceId
            };
        }
    }

    // 4) The OS default input became a headset (3.5mm jack insertion: the device id was
    //    already known, only the "default" alias label changes, so rule 2 never fires).
    if (diff.defaultInputChanged && isHeadsetLabel(diff.defaultInputChanged.toLabel)) {
        const exactRealLabel = stripDefaultPrefix(diff.defaultInputChanged.toLabel);
        const matched = ctx.snapshot.inputs.find(d =>
            isRealDeviceId(d.deviceId) && d.label.trim() === exactRealLabel);
        if (matched && !ctx.quirks.excludeAsAutoSwitchTarget(matched.label)) {
            if (ctx.cooldownActive) {
                const decision = noSwitch('default-headset-suppressed-during-cooldown', {
                    suppressedDeviceId: matched.deviceId,
                    newDefaultLabel: diff.defaultInputChanged.toLabel,
                    msSinceExplicit: ctx.msSinceExplicitSwitch
                });
                decision.handledInputId = matched.deviceId;
                return decision;
            }
            return {
                reason: 'new-paired-device',
                fallbackDeviceId: matched.deviceId,
                internalActions: [],
                rule: 'default-became-headset',
                logProps: { newDefaultLabel: diff.defaultInputChanged.toLabel },
                handledInputId: matched.deviceId
            };
        }
    }

    // 5) The OS default input changed away from a headset while our current mic IS that
    //    headset (3.5mm unplug: device id may linger in the list, only the default label
    //    moves). Emitted as 'mic-disconnected' so the C# fallback-switch path handles it.
    if (diff.defaultInputChanged &&
        diff.defaultInputChanged.fromLabel && isHeadsetLabel(diff.defaultInputChanged.fromLabel) &&
        !isHeadsetLabel(diff.defaultInputChanged.toLabel)) {
        const prevExactLabel = stripDefaultPrefix(diff.defaultInputChanged.fromLabel);
        const currentMic = ctx.snapshot.inputs.find(d => d.deviceId === ctx.currentMicId);
        if (currentMic && currentMic.label.trim() === prevExactLabel) {
            if (ctx.cooldownActive && ctx.quirks.suppressAllDuringCooldown) {
                return noSwitch('headset-removed-suppressed-during-cooldown', {
                    previousDefaultLabel: diff.defaultInputChanged.fromLabel,
                    msSinceExplicit: ctx.msSinceExplicitSwitch
                });
            }
            const newDefaultExactLabel = stripDefaultPrefix(diff.defaultInputChanged.toLabel);
            const fallbackInput = ctx.snapshot.inputs.find(d =>
                isRealDeviceId(d.deviceId) && d.label.trim() === newDefaultExactLabel);
            if (fallbackInput) {
                return {
                    reason: 'mic-disconnected',
                    fallbackDeviceId: fallbackInput.deviceId,
                    internalActions: [],
                    rule: 'default-headset-removed',
                    logProps: {
                        previousDefaultLabel: diff.defaultInputChanged.fromLabel,
                        newDefaultLabel: diff.defaultInputChanged.toLabel
                    },
                    handledInputId: null
                };
            }
        }
    }

    // 6) Nothing actionable → list refresh; on mobile re-assert output routing because the
    //    OS default route may have churned underneath the active stream.
    const decision = noSwitch('no-actionable-change');
    if (ctx.isMobile && ctx.micActive && ctx.currentMicId && !ctx.cooldownActive) {
        decision.internalActions.push('repair-output');
    }
    return decision;
}
//#endregion

//#region Switch cooldown
export class SwitchCooldown {
    private lastExplicitSwitchAt = 0;

    note(): void {
        this.lastExplicitSwitchAt = Date.now();
    }

    get msSinceExplicitSwitch(): number {
        return Date.now() - this.lastExplicitSwitchAt;
    }

    get isActive(): boolean {
        return this.msSinceExplicitSwitch < EXPLICIT_SWITCH_COOLDOWN_MS;
    }
}
//#endregion
